import React from "react";
import {
  ChevronDown,
  FileText,
  History,
  LocateFixed,
  Sparkles,
} from "lucide-react";
import type { AIAssistantWorkspaceTabId } from "../types";
import styles from "../styles/AIAssistant.module.css";

type SchoolShellContextNavigationTab = {
  id: Extract<
    AIAssistantWorkspaceTabId,
    "chat" | "artifacts" | "history" | "navigate"
  >;
  label: string;
  badgeCount?: number;
};

export type AIAssistantSchoolShellContextMenuContentProps = {
  metaLine?: string;
  headerActions?: React.ReactNode;
  navigationTabs?: readonly SchoolShellContextNavigationTab[];
  currentView?: AIAssistantWorkspaceTabId;
  onActivateTab?: (tabId: AIAssistantWorkspaceTabId) => void;
  onDismiss?: () => void;
};

const resolveNavigationIcon = (
  tabId: SchoolShellContextNavigationTab["id"],
): React.ComponentType<{ size?: number | string }> => {
  switch (tabId) {
    case "artifacts":
      return FileText;
    case "history":
      return History;
    case "navigate":
      return LocateFixed;
    case "chat":
    default:
      return Sparkles;
  }
};

const resolveNavigationDescription = (
  tabId: SchoolShellContextNavigationTab["id"],
): string => {
  switch (tabId) {
    case "chat":
      return "Return to the main transcript and prompt well.";
    case "navigate":
      return "Open route-aware starters and capability actions.";
    case "artifacts":
      return "Open generated outputs and active workspaces.";
    case "history":
    default:
      return "Review recent prompts and assistant sessions.";
  }
};

export function AIAssistantSchoolShellContextMenuContent({
  metaLine,
  headerActions,
  navigationTabs,
  currentView,
  onActivateTab,
  onDismiss,
}: AIAssistantSchoolShellContextMenuContentProps) {
  const [metaExpanded, setMetaExpanded] = React.useState(false);

  return (
    <div
      onClick={(event) => {
        const target = event.target as HTMLElement | null;
        if (
          target?.closest('[data-ai-model-option="true"]') ||
          target?.closest('[data-ai-context-menu-dismiss="true"]')
        ) {
          onDismiss?.();
        }
      }}
    >
      {metaLine ? (
        <div className="mb-1 border-b border-gray-50 px-4 py-3">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 text-left"
            data-testid="ai-school-shell-context-menu-meta-toggle"
            aria-expanded={metaExpanded}
            onClick={() => setMetaExpanded((current) => !current)}
          >
            <span className="text-[10px] uppercase tracking-widest text-irongrey/30">
              Session Context
            </span>
            <ChevronDown
              size={14}
              className={[
                "shrink-0 text-irongrey/35 transition-transform",
                metaExpanded ? "rotate-180" : "",
              ].join(" ")}
            />
          </button>
          <div
            className={[
              "transition-all duration-200",
              metaExpanded
                ? "max-h-40 pt-2 opacity-100"
                : "max-h-0 overflow-hidden opacity-0",
            ].join(" ")}
            aria-hidden={metaExpanded ? undefined : true}
          >
            <p
              className="text-[11px] leading-relaxed text-irongrey/60"
              data-testid="ai-school-shell-context-menu-meta"
              data-state={metaExpanded ? "expanded" : "collapsed"}
            >
              {metaLine}
            </p>
          </div>
        </div>
      ) : null}
      {headerActions ? (
        <div data-testid="ai-school-shell-context-menu-actions">
          {headerActions}
        </div>
      ) : null}
      {navigationTabs && navigationTabs.length > 0 ? (
        <div
          className={[
            "mb-1 border-t border-gray-50 px-2 py-2",
            styles.nodisplay,
          ].join(" ")}
          data-testid="ai-school-shell-context-menu-navigation"
        >
          <p className="px-2 pb-2 text-[10px] uppercase tracking-[0.18em] text-irongrey/32">
            Navigate
          </p>
          <div className="space-y-1">
            {navigationTabs.map((tab) => {
              const Icon = resolveNavigationIcon(tab.id);
              const isActive = currentView === tab.id;
              return (
                <button
                  key={`context-nav-${tab.id}`}
                  type="button"
                  className={[
                    "flex w-full items-center justify-between gap-3 rounded-[14px] px-3 py-2.5 text-left transition-colors",
                    isActive
                      ? "bg-primary/8 text-primary"
                      : "text-irongrey/72 hover:bg-[#f8faf9] hover:text-irongrey",
                  ].join(" ")}
                  data-testid={`ai-school-shell-context-nav-${tab.id}`}
                  data-ai-context-menu-dismiss="true"
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => {
                    onActivateTab?.(tab.id);
                    onDismiss?.();
                  }}
                >
                  <span className="inline-flex min-w-0 items-center gap-3">
                    <span
                      className={[
                        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border",
                        isActive
                          ? "border-primary/12 bg-white text-primary"
                          : "border-slate-200 bg-[#f8faf9] text-irongrey/55",
                      ].join(" ")}
                    >
                      <Icon size={16} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[12px] font-semibold leading-5">
                        {tab.label}
                      </span>
                      <span className="block text-[11px] leading-4 text-current/62">
                        {resolveNavigationDescription(tab.id)}
                      </span>
                    </span>
                  </span>
                  {typeof tab.badgeCount === "number" && tab.badgeCount > 0 ? (
                    <span className="rounded-full bg-white/90 px-2 py-1 text-[10px] font-semibold text-current shadow-[0_4px_10px_rgba(36,50,53,0.04)]">
                      {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
